/**
 * @copyright Copyright 2024-2024 Epic Systems Corporation
 * @file Usage stat info for an app
 * @module Epic.AppOrchard.Showroom.UsageStatsSection
 */

import { CalendarIcon, CheckCircleIcon, DownloadIcon, QuestionIcon } from "@chakra-ui/icons";
import { Box, Button, Flex, Stack, Text, useDisclosure } from "@chakra-ui/react";
import { showroomText } from "ao/chakraTheme";
import { AOTooltip, ConfirmationModal } from "ao/components/Core";
import { IShowroomAppDownloadModel } from "ao/types/showroom";
import { pluralIfNeeded } from "ao/utils/helpers";
import React, { FunctionComponent, memo } from "react";

interface IProps {
	appDetails: IShowroomAppDownloadModel;
	textColor: string;
}

export const UsageStatsSection: FunctionComponent<IProps> = memo((props) => {
	const { appDetails, textColor } = props;

	const badgeText = appDetails.appModel.isAutoDownloadedClient ? "Auto-download" : "Organization";

	const otherLiveCount = appDetails.liveOrgCount - appDetails.liveOrgList.length;
	const otherInstallCount = appDetails
		? appDetails.installingOrgCount - appDetails.installingOrgList.length
		: 0;

	const availableDate = appDetails.appModel.liveOn ?? appDetails.listingDate;

	return (
		<Stack
			spacing={"2px"}
			marginLeft={{ base: "unset", lg: "-5px" }}
			direction={{ base: "column", lg: "row" }}
			align="stretch"
			width="100%"
			pt="10px"
		>
			<StatBadge
				label={`${appDetails.liveOrgCount} Live ${pluralIfNeeded(
					badgeText,
					appDetails.liveOrgCount,
				)}`}
				icon={
					<AOTooltip
						helptext={`This data is organization reported and may not represent every live integration.`}
						boxProps={{
							verticalAlign: "bottom",
							paddingBottom: "2px",
						}}
					>
						{appDetails.liveOrgCount > 0 ? (
							<CheckCircleIcon color={textColor} />
						) : (
							<QuestionIcon color={textColor} />
						)}
					</AOTooltip>
				}
				modelTitle={`Live ${pluralIfNeeded(badgeText, appDetails.liveOrgCount)}`}
				modalContent={
					appDetails.liveOrgList.length > 0 ? (
						<Box maxHeight="min(400px, calc(100vh - 10rem))" overflowY="auto">
							{appDetails.liveOrgList.map((orgName) => (
								<Text>{orgName}</Text>
							))}
							{otherLiveCount > 0 && (
								<Text>{` and ${otherLiveCount} other ${pluralIfNeeded(
									"organization",
									otherLiveCount,
								)}`}</Text>
							)}
						</Box>
					) : undefined
				}
			/>
			{!appDetails.appModel.isAutoDownloadedClient && (
				<StatBadge
					label={`${appDetails.installingOrgCount} Installing ${pluralIfNeeded(
						badgeText,
						appDetails.installingOrgCount,
					)}`}
					icon={<DownloadIcon color={textColor} />}
					modelTitle={`Installing ${pluralIfNeeded(badgeText, appDetails.installingOrgCount)}`}
					modalContent={
						appDetails.installingOrgList.length > 0 ? (
							<Box maxHeight="min(400px, calc(100vh - 10rem))" overflowY="auto">
								{appDetails.installingOrgList.map((orgName) => (
									<Text>{orgName}</Text>
								))}
								{otherInstallCount > 0 && (
									<Text>{` and ${otherInstallCount} other ${pluralIfNeeded(
										"organization",
										otherInstallCount,
									)}`}</Text>
								)}
							</Box>
						) : undefined
					}
				/>
			)}
			{availableDate && (
				<StatBadge
					label={`Version available since ${new Date(availableDate).toLocaleDateString()}`}
					icon={<CalendarIcon color={textColor} />}
				/>
			)}
		</Stack>
	);
});

interface IStatBadgeProp {
	label: string;
	icon: React.ReactNode;
	modelTitle?: string;
	modalContent?: React.ReactNode;
}

const StatBadge: FunctionComponent<IStatBadgeProp> = memo((props) => {
	const { label, icon, modelTitle, modalContent } = props;

	const { isOpen, onOpen, onClose } = useDisclosure();

	return (
		<Flex paddingRight="12px" justify={{ base: "center", lg: "left" }}>
			{modalContent ? (
				<>
					<Button
						leftIcon={<Box marginRight="-3px">{icon}</Box>}
						onClick={onOpen}
						variant="linkUnderline"
						fontWeight="400"
						fontSize="1rem"
						paddingX="5px"
						colorScheme="blue"
					>
						{label}
					</Button>
					<ConfirmationModal
						title={modelTitle ?? label}
						message={modalContent}
						isOpen={isOpen}
						onAccept={onClose}
						acceptCaption="Close"
						preserveScrollBarGap
						isCentered
						returnFocusOnClose
					/>
				</>
			) : (
				<Flex
					color={showroomText}
					fontWeight="400"
					fontSize="1rem"
					paddingX="5px"
					whiteSpace="nowrap"
					height="35px"
					alignItems="center"
				>
					{icon}
					<Box ml="3px">{label}</Box>
				</Flex>
			)}
		</Flex>
	);
});
